import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/content";
import { MagneticButton } from "@/components/common/MagneticButton";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-x-0 top-0 z-50"
        data-testid="navbar"
      >
        <div
          className={`transition-colors duration-500 ${
            scrolled ? "glass-strong border-b border-white/10" : "bg-transparent"
          }`}
        >
          <nav className="container-x flex h-20 items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" aria-label="TrustaFiesta Tech home">
              <img src={CONTACT.logo} alt="TrustaFiesta Tech" className="h-11 w-11 rounded-lg object-cover ring-1 ring-white/10" />
              <span className="hidden font-heading text-lg font-extrabold tracking-tight text-white sm:block">
                Trusta<span className="text-gold">Fiesta</span>
              </span>
            </Link>

            <ul className="hidden items-center gap-8 lg:flex">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                    className="group relative py-1 text-sm font-medium text-white/70 transition-colors hover:text-white"
                  >
                    {({ isActive }) => (
                      <>
                        {link.label}
                        <span
                          className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-all duration-300 ${
                            isActive ? "w-full" : "w-0 group-hover:w-full"
                          }`}
                        />
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-3">
              <MagneticButton
                onClick={() => navigate("/contact")}
                data-testid="nav-cta"
                className="btn-magnetic group hidden bg-gold px-5 py-2.5 text-sm font-semibold text-ink hover:bg-gold-light sm:inline-flex"
              >
                Start Your Project
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </MagneticButton>

              <button
                onClick={() => setOpen(true)}
                className="grid h-11 w-11 place-items-center rounded-full border border-white/10 text-white lg:hidden"
                aria-label="Open menu"
                data-testid="mobile-menu-open"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </nav>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-ink/80 backdrop-blur-md" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
              className="glass-strong absolute right-0 top-0 h-full w-[82%] max-w-sm border-l border-white/10 p-8"
            >
              <div className="flex items-center justify-between">
                <span className="font-heading text-lg font-extrabold text-white">Menu</span>
                <button
                  onClick={() => setOpen(false)}
                  className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white"
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <ul className="mt-12 space-y-2">
                {NAV_LINKS.map((link, i) => (
                  <motion.li
                    key={link.to}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.06 }}
                  >
                    <NavLink
                      to={link.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                      className="block border-b border-white/5 py-4 font-heading text-2xl font-medium text-white/80 hover:text-gold"
                    >
                      {link.label}
                    </NavLink>
                  </motion.li>
                ))}
              </ul>
              <button
                onClick={() => { setOpen(false); navigate("/contact"); }}
                className="mt-10 w-full rounded-full bg-gold py-3.5 font-semibold text-ink"
                data-testid="mobile-cta"
              >
                Start Your Project
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
